import {BannerDataIframe} from '@core-client/ads/banner/data/BannerDataIframe';
import {BannerCfg} from '../config/BannerCfg';
import {BannerPlaceListenersProxy} from '../listener/BannerPlaceListenersProxy';
import {BannerRequestId} from './BannerRequestId';
import {BannerTransport} from './BannerTransport';
import type { TBaseParams, IBannerTransport } from './IBannerTransport';
import {createLog, getCurrentState} from '../util/BannerCommon';
import {getBannerStatLogger, getErrorOperationWithSuffix} from '../util/BannerStat';
import type {BannerRequest} from '../BannerRequest';

const log = createLog('AdfBannerTransport');

let runnings: Record<string, number> = {};
let requestIdToRunningId: Record<string, string> = {};
let lastRequestId = 1;
let lastRunningId = 0;
let respNum = 0;

const CLICK_TIMEOUT = 100;

const bannerPlaceListenersProxy = BannerPlaceListenersProxy.getInstance();

let instance: AdfBannerTransport | null = null;

export class AdfBannerTransport implements IBannerTransport {
    private bannerCfg: BannerCfg;

    constructor(bannerCfg: BannerCfg) {
        this.bannerCfg = bannerCfg;
    }

    sendRequest(slot: string, params: TBaseParams, request: BannerRequest) {
        if (!this.bannerCfg.multiLoad.adqEnabled) {
            return;
        }
        const requestId = buildRequestId();
        const requestHolder = RequestHolder.create(requestId, slot, request, params);
        this.execute(request.container, requestHolder);
    }

    private execute(container: HTMLElement, requestHolder: RequestHolder) {
        const slot = requestHolder.slot;
        /*
         * Логируем вызов РБ
         * param1 — текущая страница
         * param2 — номер слота
         */
        getBannerStatLogger().success('ad', getCurrentState(), 'js_adf.' + slot);

        const baseUrl = this.bannerCfg.multiLoad.adfTransportUrl;

        const url = `${baseUrl}${slot}?_SITEID=163&${BannerTransport.encodeParams(requestHolder.params)}`;
        this.appendIframe(container, url, requestHolder);
    }

    appendIframe(container: HTMLElement, requestUrl: string, requestHolder: RequestHolder) {
        const runningId = (lastRunningId++) + '';
        requestIdToRunningId[requestHolder.requestId] = runningId;
        const iframe = document.createElement("iframe");
        iframe.src = requestUrl + '&nn=' + respNum;
        iframe.width = '240';
        iframe.height = '400';
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('hspace', '0');
        iframe.setAttribute('marginwidth', '0');
        iframe.setAttribute('marginheight', '0');
        iframe.setAttribute('scrolling', 'no');
        iframe.id = buildWrapperId(runningId);
        container.appendChild(iframe);
        runnings[runningId] = Date.now();

        const callbacksEnabled = this.bannerCfg.callbacksEnabled;
        if (callbacksEnabled) {
            log('iframe tag appended, request: ' + requestHolder.getReqId());
            try {
                bannerPlaceListenersProxy.onRequest(requestHolder.getReqId());
            } catch (ex) {
                getBannerStatLogger().errorEx(
                    ex as Error,
                    'AdfBannerTransport',
                    'iframe_on_request',
                    getErrorOperationWithSuffix(requestHolder.slot)
                );
            }
        }

        const listener = new IframeOnLoadEventListener(iframe, requestHolder, callbacksEnabled);
        iframe.addEventListener('load', listener);
    }

    sendClick(clickUrl: string, params: TBaseParams) {
        setTimeout(() => {
            const img = new Image();
            img.src = `${clickUrl}?${BannerTransport.encodeParams(params)}`;
        }, CLICK_TIMEOUT);
    }
}

class IframeOnLoadEventListener {
    iframe: HTMLIFrameElement;
    requestHolder: RequestHolder;
    callback: CallbackJs;
    callbacksEnabled: boolean;

    constructor(iframe: HTMLIFrameElement, requestHolder: RequestHolder, callbacksEnabled: boolean) {
        this.iframe = iframe;
        this.requestHolder = requestHolder;
        this.callback = new CallbackJs(requestHolder);
        this.callbacksEnabled = callbacksEnabled;
    }

    handleEvent(e: Event) {
        const logger = getBannerStatLogger();
        const reqId = this.requestHolder.getReqId();
        log('iframe on \'' + e.type + '\' event handling, request: ' + reqId);
        try {
            if (this.callbacksEnabled) {
                bannerPlaceListenersProxy.onLoadSuccess(reqId);
            }

            this.callback.handleResponse(this.iframe);

            if (this.callbacksEnabled) {
                bannerPlaceListenersProxy.onAdsSuccess(reqId);
            }
        } catch (ex) {
            if (this.callbacksEnabled) {
                bannerPlaceListenersProxy.onScriptError(reqId);
            }
            logger.errorEx(
                ex as Error,
                'AdfBannerTransport',
                'iframe_on_load',
                getErrorOperationWithSuffix(reqId.slot)
            );
        }
        try {
            const id = this.requestHolder.requestId;
            const runningId = requestIdToRunningId[id];
            delete requestIdToRunningId[id];
            if (runningId) {
                cleanupRunning(runningId, this.requestHolder);
            }
        } catch (ex) {
            logger.errorEx(
                ex as Error,
                'AdfBannerTransport',
                'cleanup',
                getErrorOperationWithSuffix(reqId.slot)
            );
        } finally {
            this.iframe.removeEventListener('load', this);
        }
    }
}

function buildWrapperId(runningId: string): string {
    return 'adf_blwrapper_' + runningId;
}

function buildRequestId(): string {
    return 'nbnr' + (lastRequestId++);
}

function cleanupRunning(runningId: string, requestHolder: RequestHolder) {
    try {
        delete runnings[runningId];

        const elementId = buildWrapperId(runningId);
        const wrapper = document.getElementById(elementId);

        if (wrapper !== null) {
            respNum++;
        }
    } catch (e) {
        getBannerStatLogger().errorEx(
            e as Error,
            'AdfBannerTransport',
            'cleanup_running',
            getErrorOperationWithSuffix(requestHolder.slot)
        );
        throw e;
    }
}

class CallbackJs {
    request: BannerRequest;

    constructor(requestHolder: RequestHolder) {
        this.request = requestHolder.request;
        this.request.setCancellationListener(this);
    }

    handleResponse(iframe: HTMLElement) {
        const bannerData = new BannerDataIframe(iframe.id);
        this.request.handleBannerDataResponse(bannerData);
    }

    onCancel() {
    }
}

class RequestHolder {
    slot!: string;
    requestId!: string;
    request!: BannerRequest;
    params: TBaseParams;

    constructor() {
        this.params = {};
    }

    static create(requestId: string, slot: string, request: BannerRequest, params: TBaseParams) {
        const holder = new RequestHolder();
        holder.slot = slot;
        holder.requestId = requestId;
        holder.request = request;
        const keys = Object.getOwnPropertyNames(params);
        if (keys.length) {
            keys.forEach(key => {
                holder.params[key] = params[key];
            });
        }
        BannerTransport.appendCookielessUserSyncParameters(holder.params);
        return holder;
    }

    getReqId() {
        return new BannerRequestId(this.requestId, this.request.bannerPlace!, this.slot, this.request.container)
            .setTransport('js_adf');
    }
}
